import {useCallback, useEffect, useRef, useState} from 'react';
import {ChevronLeft, ListFilter, Search, Sparkles} from 'lucide-react';
import {
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuTrigger,
  Input,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@tinct/web-components';
import {cn} from '@tinct/core-utils';
import {usePublishPage, useRegeneratePage, useUnpublishPage} from '@tinct/core-api';
import {useGetCurrentWorkspaceId} from '@tinct/core-workspace';
import {useStudioHostUrls} from '../studio-host-context';
import {STATUS_FILTER_OPTIONS, usePageSearch} from '../hooks';
import type {StatusFilterValue} from '../hooks';
import {PageItem} from './page-item';
import {UnpublishPageModal} from '../../../pages/shared/unpublish-page-modal';
import {getPageDisplayIdentity} from '../../../pages/shared';
import {EmbeddedPage, ReadCampaign} from "@tinct/core-api-models";
import {toast} from 'sonner';

type PanelCampaign = Pick<ReadCampaign, 'id' | 'status' | 'snippetStatus'>;

/**
 * Radix popper content (menus, tooltips) and dialogs — portaled out of the
 * panel — that are actually open. Popper wrappers linger while their exit
 * animation plays, so the check keys on the content's `data-state`.
 */
const OPEN_OVERLAY_SELECTOR =
  '[data-radix-popper-content-wrapper] [data-state="open"], [role="dialog"][data-state="open"], [role="alertdialog"][data-state="open"]';

type PagesSidePanelProps = {
  pages: EmbeddedPage[];
  /** Owner of the pages: its id builds the preview route, its status gates Preview. */
  campaign: PanelCampaign;
  selectedPageId: string | null;
  /**
   * Icon rail vs full list — driven by hover (enter expands, leave
   * collapses). Owned by the host: the panel is rendered inside the page
   * components, which remount on view-mode / document changes.
   */
  collapsed: boolean;
  onCollapsedChange: (collapsed: boolean) => void;
  onSelectPage?: (page: EmbeddedPage) => void;
  onGenerateAll?: () => void;
  isGenerating?: boolean;
  onToggleExclude?: (page: EmbeddedPage) => void;
  initialSearchQuery?: string;
  /** ADWORD campaigns: rows are ads-campaign variants, not target accounts. */
  isAdwordAxis?: boolean;
};

const SidebarGroupLabel = ({ label }: { label: string }) => (
  <div className="flex h-9 shrink-0 items-center px-2">
    <p className="text-xs font-medium text-muted-foreground">{label}</p>
  </div>
);

/**
 * Detects whether a scrollable element has overflowing content.
 */
function useHasOverflow() {
  const [hasOverflow, setHasOverflow] = useState(false);
  const observerRef = useRef<ResizeObserver | null>(null);

  const ref = useCallback((node: HTMLDivElement | null) => {
    observerRef.current?.disconnect();
    if (!node) return;

    const check = () => setHasOverflow(node.scrollHeight > node.clientHeight);

    check();
    observerRef.current = new ResizeObserver(check);
    observerRef.current.observe(node);
  }, []);

  return { ref, hasOverflow };
}

/**
 * Scrollable page list with conditional bottom fade gradient.
 */
const PageList = ({
  pages,
  campaign,
  selectedPageId,
  collapsed,
  disableHover,
  onSelectPage,
  onPreview,
  onRetry,
  onToggleExclude,
  onPublish,
  onUnpublish,
}: {
  pages: EmbeddedPage[];
  campaign: PanelCampaign;
  selectedPageId?: string | null;
  collapsed?: boolean;
  disableHover?: boolean;
  onSelectPage?: (page: EmbeddedPage) => void;
  /** Omitted when the host has no preview page: the action is hidden. */
  onPreview?: (page: EmbeddedPage) => void;
  onRetry: (page: EmbeddedPage) => void;
  onToggleExclude?: (page: EmbeddedPage) => void;
  onPublish: (page: EmbeddedPage) => void;
  onUnpublish: (page: EmbeddedPage) => void;
}) => {
  const { ref, hasOverflow } = useHasOverflow();

  return (
    <div className="relative min-h-0 flex-1">
      <div ref={ref} className="absolute inset-0 overflow-y-auto">
        {pages.map((page) => (
          <PageItem
            key={page.id}
            page={page}
            campaign={campaign}
            isSelected={page.id === selectedPageId}
            collapsed={collapsed}
            disableHover={disableHover}
            onClick={onSelectPage}
            onPreview={onPreview}
            onRetry={onRetry}
            onToggleExclude={onToggleExclude}
            onPublish={onPublish}
            onUnpublish={onUnpublish}
          />
        ))}
      </div>
      {hasOverflow && (
        <div className="pointer-events-none absolute bottom-0 left-0 right-0 h-20 bg-gradient-to-t from-background to-transparent" />
      )}
    </div>
  );
};

/**
 * The panel listing the campaign's variant pages: the generated variants
 * first, then the not-yet-generated target accounts. Shown only in the
 * studio's "Page variants" mode (the top-bar tabs switch back to the original
 * page). Sits inside the canvas card, flush with its left edge: the collapsed
 * icon rail reserves in-flow width beside the page preview, and hovering it
 * expands the full list over the preview without shifting it.
 */
export const PagesSidePanel = ({
  pages,
  campaign,
  selectedPageId,
  collapsed,
  onCollapsedChange,
  onSelectPage,
  onGenerateAll,
  isGenerating,
  onToggleExclude,
  initialSearchQuery,
  isAdwordAxis = false,
}: PagesSidePanelProps) => {
  const workspaceId = useGetCurrentWorkspaceId();
  const { variantPreviewUrl } = useStudioHostUrls(campaign.id ?? '');
  const {
    searchQuery,
    setSearchQuery,
    statusFilter,
    setStatusFilter,
    isFiltered,
    activeFilterLabel,
    targetPages,
    queuedPages,
    hasAnyTarget,
    totalCount,
  } = usePageSearch(pages, initialSearchQuery);

  const regeneratePage = useRegeneratePage({
    onSuccess: () => toast.success('Page regeneration started'),
    onError: () => toast.error('Failed to regenerate page'),
  });

  const unpublishPage = useUnpublishPage({
    onSuccess: () => toast.success('Variant unpublished'),
    onError: (error) => {
      console.error('[unpublishPage] failed:', error);
      toast.error('Failed to unpublish variant');
    },
  });

  const publishPage = usePublishPage({
    onSuccess: () => toast.success('Variant published'),
    onError: () => toast.error('Failed to publish variant'),
  });

  const handleRetry = (page: EmbeddedPage) => {
    if (page.id) {
      regeneratePage.mutate({ pageId: page.id, organizationId: workspaceId });
    }
  };

  // The way back from `unpublished`; no confirmation, Unpublish is one click away.
  const handlePublish = (page: EmbeddedPage) => {
    if (page.id) {
      publishPage.mutate({ pageId: page.id });
    }
  };

  // Same internal preview route as the dashboard row actions. Hosts without
  // that route (back office) hide the action.
  const handlePreview = variantPreviewUrl
    ? (page: EmbeddedPage) => {
        if (!page.id) return;
        window.open(variantPreviewUrl(page.id), '_blank', 'noopener,noreferrer');
      }
    : undefined;

  // Confirm before taking a single live variant offline (mirrors the shared
  // PageActionsMenu behavior).
  const [pendingUnpublish, setPendingUnpublish] = useState<EmbeddedPage | null>(null);

  const handleUnpublish = (page: EmbeddedPage) => {
    setPendingUnpublish(page);
  };

  const handleUnpublishConfirmed = () => {
    if (!pendingUnpublish?.id) return;
    unpublishPage.mutate(
      { pageId: pendingUnpublish.id },
      { onSettled: () => setPendingUnpublish(null) },
    );
  };

  // Hover drives the two states: the pointer entering the rail expands it,
  // leaving collapses it. Menus, tooltips and dialogs opened from the panel
  // are portaled to <body>, so the pointer "leaves" while they're in use —
  // the collapse waits until no such overlay is open and the pointer really
  // is outside, re-checking on a short timer only while one is open.
  const rootRef = useRef<HTMLDivElement>(null);
  const collapseTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const clearCollapseTimer = useCallback(() => {
    if (collapseTimerRef.current != null) clearTimeout(collapseTimerRef.current);
    collapseTimerRef.current = null;
  }, []);
  const handleMouseEnter = useCallback(() => {
    clearCollapseTimer();
    onCollapsedChange(false);
  }, [clearCollapseTimer, onCollapsedChange]);
  const handleMouseLeave = useCallback(() => {
    clearCollapseTimer();
    const tick = () => {
      collapseTimerRef.current = null;
      const root = rootRef.current;
      if (!root || root.matches(':hover')) return;
      if (document.querySelector(OPEN_OVERLAY_SELECTOR)) {
        collapseTimerRef.current = setTimeout(tick, 250);
        return;
      }
      onCollapsedChange(true);
    };
    tick();
  }, [clearCollapseTimer, onCollapsedChange]);
  useEffect(() => clearCollapseTimer, [clearCollapseTimer]);

  const hasQueued = queuedPages.length > 0;
  // Keep the generated section mounted while filtering so the back button stays reachable.
  const showTarget = isFiltered || hasAnyTarget;
  const bothVisible = showTarget && hasQueued;
  const isSearchActive = searchQuery.trim().length > 0;
  const hasNoSearchResults = isSearchActive && targetPages.length === 0;

  return (
    // In-flow spacer at rail width: the page preview always sits beside the
    // collapsed rail. The body overlays the preview when expanded, its width
    // transitioned so the open/close reads as a slide rather than a jump.
    // z-50 keeps it above the generating overlay (z-40) so the list stays
    // reachable.
    <div
      ref={rootRef}
      className="relative h-full w-[68px] shrink-0"
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      <div
        className={cn(
          'dark absolute inset-y-0 left-0 z-50 flex flex-col gap-2 overflow-hidden border-r border-border-subtle bg-background p-2 text-foreground transition-[width] duration-300 ease-in-out',
          collapsed ? 'w-[68px]' : 'w-[224px] shadow-lg'
        )}
      >
        {/* Search + status filter (expanded) / expand button (collapsed) */}
        <div className="flex shrink-0 items-center gap-2 p-2">
          {collapsed ? (
            // Stand-in for the search field on the rail; hovering the rail is
            // what expands it, the click just makes that explicit.
            <Button
              variant="outline"
              size="icon"
              className="size-9"
              onClick={() => onCollapsedChange(false)}
              aria-label="Search pages"
            >
              <Search className="size-4" />
            </Button>
          ) : (
            <>
              <div className="relative flex-1">
                <Search className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
                <Input
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Search..."
                  className="h-9 pl-9"
                />
              </div>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="outline" size="icon" className="size-9 shrink-0">
                    <ListFilter className="size-4" />
                  </Button>
                </DropdownMenuTrigger>
                {/* Portaled out of the dark panel, so it carries the theme
                    itself. Opens under the button, flush with its left edge,
                    at the panel's width (Figma). */}
                <DropdownMenuContent align="start" className="dark w-56">
                  <DropdownMenuRadioGroup
                    value={statusFilter}
                    onValueChange={(value) => setStatusFilter(value as StatusFilterValue)}
                  >
                    {STATUS_FILTER_OPTIONS.map((option) => (
                      <DropdownMenuRadioItem key={option.value} value={option.value}>
                        {option.label}
                      </DropdownMenuRadioItem>
                    ))}
                  </DropdownMenuRadioGroup>
                </DropdownMenuContent>
              </DropdownMenu>
            </>
          )}
        </div>

        {/* Two independently scrollable sections sharing available height,
            each closed by a rule (the same structure on the collapsed rail) */}
        <div className="flex min-h-0 flex-1 flex-col border-t border-border-subtle">
          {/* Generated variants - kept mounted while filtering so the back button stays reachable */}
          {showTarget && (
            <div className={`flex min-h-0 flex-col border-b border-border-subtle py-2 ${bothVisible ? 'h-1/2' : 'flex-1'}`}>
              {collapsed ? null : isFiltered ? (
                <div className="flex h-8 shrink-0 items-center gap-0.5 pr-2">
                  <Button
                    variant="ghost"
                    size="icon"
                    className="size-7 text-muted-foreground"
                    onClick={() => setStatusFilter('all')}
                  >
                    <ChevronLeft className="size-4" />
                  </Button>
                  <p className="text-xs font-medium text-muted-foreground">{activeFilterLabel}</p>
                </div>
              ) : (
                <SidebarGroupLabel label="Generated" />
              )}
              {hasNoSearchResults ? (
                <div className="px-2 py-4 text-xs text-muted-foreground">
                  No pages match &ldquo;{searchQuery}&rdquo;.
                </div>
              ) : (
                <PageList
                  pages={targetPages}
                  campaign={campaign}
                  selectedPageId={selectedPageId}
                  collapsed={collapsed}
                  onSelectPage={onSelectPage}
                  onPreview={handlePreview}
                  onRetry={handleRetry}
                  onToggleExclude={onToggleExclude}
                  onPublish={handlePublish}
                  onUnpublish={handleUnpublish}
                />
              )}
            </div>
          )}

          {/* Not-generated-yet targets (accounts or matched ads campaigns) -
              unaffected by search or filter */}
          {hasQueued && (
            <div className={`flex min-h-0 flex-col border-b border-border-subtle py-2 ${bothVisible ? 'h-1/2' : 'flex-1'}`}>
              {!collapsed && (
                <SidebarGroupLabel label={isAdwordAxis ? 'Ads campaigns' : 'Target accounts'} />
              )}
              <PageList
                pages={queuedPages}
                campaign={campaign}
                selectedPageId={selectedPageId}
                collapsed={collapsed}
                disableHover
                onSelectPage={onSelectPage}
                onPreview={handlePreview}
                onRetry={handleRetry}
                onToggleExclude={onToggleExclude}
                onPublish={handlePublish}
                onUnpublish={handleUnpublish}
              />
            </div>
          )}
        </div>

        {/* ADWORD campaigns start with no variants: matching happens at every
            ads sync, so an empty list is a waiting state, not a dead end.
            Keyed on the raw page list — `totalCount` only counts pending
            generation work and drops to 0 while variants generate. */}
        {isAdwordAxis && pages.length === 0 && !collapsed && (
          <div className="px-2 py-4 text-xs text-muted-foreground">
            No ads campaigns matched yet — variants appear automatically after
            each sync.
          </div>
        )}

        {/* Generate all button - sticky at bottom */}
        {totalCount > 0 && (
          <div className="shrink-0 p-2">
            {collapsed ? (
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="outline"
                    size="icon"
                    className="size-9"
                    onClick={onGenerateAll}
                    disabled={isGenerating}
                    aria-label={`Generate all (${totalCount})`}
                  >
                    <Sparkles className="size-4" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent side="right">Generate all ({totalCount})</TooltipContent>
              </Tooltip>
            ) : (
              <Button
                variant="outline"
                className="w-full"
                onClick={onGenerateAll}
                disabled={isGenerating}
              >
                <Sparkles className="size-4" />
                Generate all <span className="text-muted-foreground">({totalCount})</span>
              </Button>
            )}
          </div>
        )}

        <UnpublishPageModal
          open={pendingUnpublish !== null}
          onOpenChange={(open) => !open && setPendingUnpublish(null)}
          pageName={pendingUnpublish ? getPageDisplayIdentity(pendingUnpublish).label : undefined}
          isPending={unpublishPage.isPending}
          onConfirm={handleUnpublishConfirmed}
        />
      </div>
    </div>
  );
};
